import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BadgeCheck, ChevronRight, Download, Eye, EyeOff, Info, MapPin, Pencil, Plus, Printer, ShieldAlert } from 'lucide-react';
import * as hr from '../api';
import { useHR, useLookup } from '../HRContext';
import { errorText } from '../../utils';
import RecordForm from '../components/Form';
import { RecordDetail } from '../components/Register';
import StatusPill from '../components/StatusPill';
import Picker from '../components/Picker';
import { RESOURCES } from '../resources';
import { daysUntil, fmtDate, fmtMoney, has, initials, monthsSince, titleCase, today } from '../format';
import { hrSubUrl } from '../catalog';
import Icon from '../Icon';
import { TABS, Tiles, expiringItems } from './E360Tabs';
import { completeness } from './CompleteEmployeeRecords';
import Loader from '../components/Loader';
import { useFileUrl } from '../components/Files';
import EmployeePrintView from '../components/EmployeePrintView';

// Everything about one employee in one view — the port of Stephanet's Employee 360 profile.
// Tabs follow the reference order; tabs for modules not built yet (payroll, loans, assets, tools) are left out.

const ORDER = ['overview', 'personal', 'employment', 'reporting', 'sites', 'probation', 'references', 'expiry', 'financial', 'salary',
  'leave', 'attendance', 'documents', 'performance', 'jobs', 'work', 'queries', 'accommodation', 'idcards'];
// overview shortcuts, as on the reference profile: [tab, label, icon, colour]
const MODULES = [['salary', 'Salary History', 'trending-up', 'blue'], ['leave', 'Leave Records', 'calendar', 'teal'], ['attendance', 'Attendance', 'clock', 'indigo'],
  ['performance', 'Performance', 'star', 'yellow'], ['queries', 'Query Reports', 'message-square-warning', 'red'], ['documents', 'Documents', 'file-text', 'slate'],
  ['sites', 'Site Access', 'map-pin', 'green'], ['accommodation', 'Accommodation', 'building-2', 'purple'], ['jobs', 'Job History', 'history', 'amber'], ['idcards', 'ID Card & HMO', 'id-card', 'blue']];
const EMPTY = { documents: [], salary: [], reviews: [], cases: [], leave: [], allocations: [], attendance: [], idcard: null, permits: [], hmo: [], accommodation: [] };

const iso = d => d.toISOString().slice(0, 10);
const ago = n => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const quiet = p => p.catch(() => null);

async function loadSections(id) {
  const year = new Date().getFullYear();
  const [documents, salary, reviews, cases, leave, allocations, attendance, idcard, permits, hmo, accommodations] = await Promise.all([
    quiet(hr.listEmployeeDocuments(id)), quiet(hr.listSalaryHistory(id)), quiet(hr.listReviews({ employee_id: id })), quiet(hr.listCases({ employee_id: id })),
    quiet(hr.listLeaveRequests({ employee_id: id })), quiet(hr.getLeaveBalances(id, year)),
    quiet(hr.listAttendanceRecords({ employee_id: id, start_date: ago(90), end_date: iso(new Date()) })), quiet(hr.getEmployeeIdCard(id)),
    quiet(hr.listSitePermits({ employee_id: id })), quiet(hr.listHmo()), quiet(hr.listAccommodations()),
  ]);
  const mine = rows => (rows || []).filter(r => String(r.employee_id) === String(id));
  return {
    documents: documents || [], salary: salary || [], reviews: reviews || [], cases: cases || [], leave: leave || [], allocations: allocations || [],
    attendance: attendance || [], idcard: idcard && idcard.id ? idcard : null, permits: permits || [], hmo: mine(hmo),
    accommodation: mine((accommodations || []).flatMap(a => (a.active_assignments || []).map(x => ({ ...x, property_name: x.property_name || a.property_name })))),
  };
}

// the employee's profile photo as an object URL (null when there is none), for the hero and the print layout
function usePhotoUrl(employeeId) {
  const [photoId, setPhotoId] = useState(null);
  useEffect(() => {
    let live = true;
    setPhotoId(null);
    if (employeeId) hr.listEmployeeFiles(employeeId).then(list => { if (live) setPhotoId(list.find(f => f.category === 'photo')?.id || null); }).catch(() => {});
    return () => { live = false; };
  }, [employeeId]);
  return useFileUrl(() => hr.photoBlob(employeeId), photoId);
}

export default function Employee360() {
  const { canManage, notify, user } = useHR();
  const [params, setParams] = useSearchParams();
  const id = params.get('id') || '';
  const tab = params.get('tab') || 'overview';
  const setTab = t => setParams(t === 'overview' ? { id } : { id, tab: t });
  const setId = v => setParams(v ? { id: v } : {});
  const people = useLookup('employees') || [];
  const [e, setE] = useState(null);
  const photoUrl = usePhotoUrl(e?.id);
  const [busy, setBusy] = useState(''); // 'pdf' | 'print' while the report is being prepared
  const [printing, setPrinting] = useState(false);
  const [d, setD] = useState(EMPTY);
  const [error, setError] = useState('');
  const [reveal, setReveal] = useState(false);
  const [adding, setAdding] = useState(null);
  const [opened, setOpened] = useState(null);

  const load = useCallback(() => {
    if (!id) return;
    setError('');
    hr.getEmployee(id).then(setE).catch(err => { setE(null); setError(errorText(err)); });
    loadSections(id).then(setD);
  }, [id]);
  useEffect(() => { setE(null); setD(EMPTY); setReveal(false); load(); }, [load]);

  const ctx = e && {
    e, d, people, canManage, setTab, notify, reload: load,
    add: (res, defaults) => setAdding({ res, defaults }),
    open: (res, row) => setOpened({ res, row }),
  };
  const tabs = useMemo(() => (!ctx ? [] : ORDER.map(key => {
    if (key === 'overview') return { key, label: 'Overview', icon: 'user' };
    const [label, icon, , count] = TABS[key];
    return { key, label, icon, count: count ? count(ctx) : undefined };
  })), [e, d]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = tabs.find(t => t.key === tab) ? tab : 'overview';

  if (!id) {
    return <div className="panel toolbar"><span className="field-label">Employee</span><Picker value={id} onChange={setId} placeholder="Search and select an employee…" /></div>;
  }

  const monthly = e ? Number(e.monthly_gross ?? e.basic_salary) || 0 : 0;
  const base = e ? Number(e.basic_salary) || 0 : 0;
  const comp = e?.compensation || {};
  const pd = e?.profile_detail || {};
  const months = e && monthsSince(e.hire_date);
  const hidden = v => (reveal ? v : '₦ ***,***');
  const bank = e?.statutory_and_bank || {};

  const alerts = [];
  if (e) {
    const missing = [['bank_account_number', 'bank'], ['pension_rsa_pin', 'pension'], ['tax_identification_number', 'tax']].filter(([n]) => !has(bank[n])).map(([, l]) => l);
    if (missing.length) alerts.push({ tone: 'bad', icon: ShieldAlert, text: `Missing ${missing.join(' / ')} details`, tab: 'financial' });
    const dated = expiringItems(e, d.documents);
    const expired = dated.filter(x => x.left !== null && x.left < 0).length;
    const soon = dated.filter(x => x.left !== null && x.left >= 0 && x.left <= 30).length;
    if (expired) alerts.push({ tone: 'bad', icon: AlertTriangle, text: `${expired} document${expired > 1 ? 's' : ''} expired`, tab: 'expiry' });
    if (soon) alerts.push({ tone: 'warn', icon: AlertTriangle, text: `${soon} document${soon > 1 ? 's' : ''} expiring within 30 days`, tab: 'expiry' });
    if (e.employment_status === 'probation' && e.probation_end_date) {
      const left = daysUntil(e.probation_end_date);
      if (left <= 30) alerts.push({ tone: left < 0 ? 'bad' : 'warn', icon: Info, text: left < 0 ? `Probation review overdue (ended ${fmtDate(e.probation_end_date)})` : `Probation review due ${fmtDate(e.probation_end_date)}`, tab: 'probation' });
    }
    const pendingRefs = (e.references || []).filter(r => r.status === 'pending').length;
    if (pendingRefs) alerts.push({ tone: 'warn', icon: Info, text: `${pendingRefs} reference${pendingRefs > 1 ? 's' : ''} awaiting verification`, tab: 'references' });
    const unverified = d.documents.filter(x => !x.is_verified).length;
    if (unverified) alerts.push({ tone: 'warn', icon: Info, text: `${unverified} document${unverified > 1 ? 's' : ''} awaiting verification`, tab: 'documents' });
    if (['draft', 'onboarding'].includes(e.employment_status)) alerts.push({ tone: 'warn', icon: Info, text: 'Onboarding record — complete and submit it', to: canManage ? hrSubUrl('add-employee', { id: e.id }) : null });
    const pendingLeave = d.leave.filter(x => ['pending', 'pre_approved'].includes(x.status)).length;
    if (pendingLeave) alerts.push({ tone: 'info', icon: Info, text: `${pendingLeave} leave request${pendingLeave > 1 ? 's' : ''} awaiting approval`, tab: 'leave' });
    const gaps = completeness(e).missing;
    if (gaps.length) alerts.push({ tone: 'warn', icon: Info, text: `Record incomplete — ${gaps.map(([, l]) => l).slice(0, 3).join(', ')}${gaps.length > 3 ? ` +${gaps.length - 3} more` : ''}`, to: hrSubUrl('complete-records') });
    if (!e.user_email) alerts.push({ tone: 'info', icon: Info, text: 'No system login linked' });
    else if (e.user_is_active === false) alerts.push({ tone: 'warn', icon: ShieldAlert, text: 'Login disabled' });
    if (e.is_private) alerts.push({ tone: 'info', icon: ShieldAlert, text: 'Private record — restricted access' });
    if (!alerts.some(a => a.tone !== 'info')) alerts.unshift({ tone: 'good', icon: BadgeCheck, text: 'No outstanding issues on this profile' });
  }

  // Download Report: the PDF profile report built on the server (ReportLab), photo included
  const downloadReport = async () => {
    setBusy('pdf');
    try {
      const url = URL.createObjectURL(await hr.employeeReportBlob(e.id));
      const a = Object.assign(document.createElement('a'), { href: url, download: `${e.employee_no} - ${e.full_name}.pdf`.replace(/[/\\]/g, '-') });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) { notify(`Could not download the report — ${errorText(err)}`, 'bad'); }
    setBusy('');
  };
  // Print: the branded print layout (components/EmployeePrintView), the only thing on the page while printing
  const print = () => {
    setBusy('print'); setPrinting(true);
    requestAnimationFrame(() => requestAnimationFrame(async () => {
      const img = document.querySelector('.ep-print-root img.ep-photo');
      if (img && !img.complete) await new Promise(r => { img.onload = r; img.onerror = r; });
      window.print();
      setPrinting(false); setBusy('');
    }));
  };


  const addRes = adding && RESOURCES[adding.res];
  const addFields = addRes && (addRes.createFields || addRes.fields.filter(f => f.form !== false && f.form !== 'edit' && f.name !== 'employee_id'));

  return (
    <div className="e360">
      <div className="e360-head">
        <div className="e360-head-left">
          <Link className="back-btn" title="Back to Employees" to={hrSubUrl('manage-employees')}><ArrowLeft size={17} /></Link>
          <span className="back-label">Back to Employees</span>
          <div className="e360-switch"><Picker value={id} onChange={setId} placeholder="Switch employee…" /></div>
        </div>
        <div className="e360-head-actions">
          {e && <>
            <button className="btn" onClick={downloadReport} disabled={!!busy}><Download size={15} /> {busy === 'pdf' ? 'Preparing PDF…' : 'Download Report'}</button>
            <button className="btn" onClick={print} disabled={!!busy}><Printer size={15} /> {busy === 'print' ? 'Preparing…' : 'Print'}</button>
            {canManage && <Link className="btn primary" to={hrSubUrl('add-employee', { id: e.id })}><Pencil size={15} /> Edit Employee</Link>}
          </>}
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {printing && e && <EmployeePrintView e={e} documents={d.documents} photoUrl={photoUrl} printedBy={[user?.first_name, user?.last_name].filter(Boolean).join(' ')} />}
      {!e && !error && <Loader title="Loading employee…" subtitle="Building the 360 profile" />}

      {e && (
        <>
          <div className="e360-hero">
            <div className="hero-top">
              <div className="hero-avatar">{photoUrl ? <img src={photoUrl} alt={e.full_name} /> : initials(e.full_name)}</div>
              <div className="grow">
                <div className="hero-name">{e.full_name} <StatusPill value={e.employment_status} /></div>
                <div className="hero-sub">{[e.designation_title || pd.job_title, e.department_name].filter(Boolean).join(' · ') || '—'}</div>
                {e.site_name && <div className="hero-sub"><MapPin size={13} /> {e.site_name}</div>}
              </div>
              {canManage && (
                <div className="hero-actions">
                  <button className="btn hero" onClick={() => ctx.add('leave_requests', {})}><Plus size={14} /> Add Leave</button>
                  <button className="btn hero" onClick={() => ctx.add('hr_cases', { case_type: 'disciplinary', effective_date: today() })}><Plus size={14} /> Raise Query</button>
                  <button className="btn hero" onClick={() => ctx.add('salary_changes', { effective_date: today() })}><Plus size={14} /> Salary Change</button>
                </div>
              )}
            </div>
            <div className="hero-facts">
              {[['Salary Type', titleCase(e.salary_type)], ['Employment Type', titleCase(e.employment_type)], ['Mgmt Type', pd.management_type],
                ['Level', pd.employee_level], ['Pay Grade', comp.pay_grade], ['Staff Category', pd.staff_category]]
                .map(([l, v]) => <span key={l} className="hero-fact">{l}: <b>{v || '—'}</b></span>)}
            </div>
          </div>

          <div className="e360-stats">
            <div><Icon name="wallet" size={18} /><b>{hidden(fmtMoney(monthly))}</b><span>Monthly Salary</span></div>
            <div><Icon name="banknote" size={18} /><b>{hidden(fmtMoney(base))}</b><span>{e.salary_type === 'daily' ? 'Base (monthly ref.)' : 'Base Salary'}</span></div>
            <div><Icon name="trending-up" size={18} /><b>{hidden(fmtMoney(monthly * 12))}</b><span>Annual Salary</span></div>
            <div><Icon name="calendar" size={18} /><b>{months === null ? '—' : `${months} mo`}</b><span>Total Months</span></div>
            <div><Icon name="id-card" size={18} /><b>{e.employee_no}</b><span>Employee ID</span></div>
            <button className="icon-btn" title={reveal ? 'Hide salary' : 'Show salary'} onClick={() => setReveal(r => !r)}>{reveal ? <EyeOff size={17} /> : <Eye size={17} />}</button>
          </div>

          <div className="e360-alerts">
            {alerts.map(a => {
              const A = a.icon;
              const body = <><A size={14} /> {a.text}</>;
              if (a.to) return <Link key={a.text} className={`alert-chip ${a.tone}`} to={a.to}>{body}</Link>;
              return a.tab
                ? <button key={a.text} className={`alert-chip ${a.tone}`} onClick={() => setTab(a.tab)}>{body}</button>
                : <span key={a.text} className={`alert-chip ${a.tone}`}>{body}</span>;
            })}
          </div>

          <div className="tabs e360-tabs" role="tablist">
            {tabs.map(t => (
              <button key={t.key} role="tab" className={`nav-btn ${current === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                <Icon name={t.icon} size={15} /> <span>{t.label}</span>{t.count !== undefined && <span className="tab-count">{t.count}</span>}
              </button>
            ))}
          </div>

          {current === 'overview' && (
            <div className="e360-overview">
              <div>
                <div className="panel">
                  <h3 className="panel-title">Key Details</h3>
                  <Tiles items={[['Employee ID', e.employee_no], ['Department', e.department_name], ['Designation', e.designation_title], ['Employment Type', titleCase(e.employment_type)],
                    ['Salary Type', titleCase(e.salary_type)], ['Primary Project Site', e.site_name], ['Reports To', e.manager_name], ['Date of Employment', fmtDate(e.hire_date)],
                    ['Status', <StatusPill value={e.employment_status} />], ['Work Email', e.work_email], ['Work Phone', e.work_phone], ['Branch', e.branch_name],
                    ['Departments', (e.departments || []).map(x => x.name + (x.is_primary ? ' (primary)' : '')).join(', ')],
                    ['System Login', e.user_email ? `${e.user_email}${e.user_is_active === false ? ' — disabled' : ''}` : 'Not linked']]} />
                </div>
                <div className="panel">
                  <h3 className="panel-title">Module Connections</h3>
                  <div className="module-grid">
                    {MODULES.map(([key, text, icon, color]) => {
                      const t = tabs.find(x => x.key === key);
                      if (!t) return null;
                      return (
                        <button key={key} className={`module-tile lc-${color}`} onClick={() => setTab(key)}>
                          <span className="lc-icon"><Icon name={icon} size={17} /></span>
                          <span><b>{text}</b>{t.count !== undefined && <small>{t.count} record{t.count === 1 ? '' : 's'}</small>}</span>
                          <ChevronRight size={16} className="module-chev" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div>
                <div className="panel list-card">
                  <h3 className="panel-title">Financial Snapshot</h3>
                  {[['Monthly Salary', monthly, 'brand'], ['Base Salary', base, 'blue'], ['Housing', comp.housing_allowance], ['Transport', comp.transport_allowance],
                    ['Meal', comp.meal_allowance], ['Medical', comp.medical_allowance],
                    ...(Number(comp.utility_allowance) ? [['Utility', comp.utility_allowance]] : []),
                    ...(Number(comp.other_allowance) ? [['Other Allowances', comp.other_allowance]] : []),
                    ...(e.salary_type === 'daily' ? [['Daily Rate', comp.daily_rate]] : [])].map(([l, v, tone]) => (
                    <div key={l}><span>{l}</span><b className={tone ? `fs-${tone}` : !Number(v) ? 'fs-zero' : ''}>{hidden(fmtMoney(Number(v) || 0))}</b></div>
                  ))}
                </div>
                <div className="panel list-card">
                  <h3 className="panel-title">Bank & Statutory</h3>
                  {[['Bank Name', bank.bank_name], ['Account Number', bank.bank_account_number], ['Pension ID / RSA Number', bank.pension_rsa_pin],
                    ['Tax Identification Number (TIN)', bank.tax_identification_number], ['NHF Number', bank.nhf_number], ['NHIA Number', comp.nhia_number]]
                    .map(([l, v]) => <div key={l}><span>{l}</span><b>{v || '—'}</b></div>)}
                </div>
              </div>
            </div>
          )}

          {current !== 'overview' && <div className="e360-tab">{TABS[current][2](ctx)}</div>}

          {adding && (
            <RecordForm title={`New ${addRes.label} — ${e.full_name}`} fields={addFields} initial={adding.defaults}
                        onClose={() => setAdding(null)}
                        onSubmit={async payload => {
                          await addRes.create({ ...adding.defaults, ...payload, employee_id: e.id });
                          notify(`${addRes.label} created`);
                          setAdding(null); load();
                        }} />
          )}
          {opened && (
            <RecordDetail res={RESOURCES[opened.res]} record={opened.row} onClose={() => setOpened(null)}
                          onChanged={() => { setOpened(null); load(); }} />
          )}
        </>
      )}
    </div>
  );
}
